import { Link } from "react-router-dom";
import { ArrowRight, Search, Hammer, KeyRound, Calculator } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { CtaSection } from "../components/CtaSection";
import { CinematicBreak } from "../components/CinematicBreak";
import { usePageMeta } from "../hooks/usePageMeta";
import { IMAGES, CINEMATIC } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";

const PILLARS = [
  {
    icon: Search,
    num: "01",
    title: "Property Search & Sourcing",
    text: "We find suitable property opportunities based on your goals, budget and preferred location — assessing each option carefully before it reaches you.",
    img: IMAGES.townhouse,
    alt: "Elegant London townhouse facade in warm natural light",
  },
  {
    icon: Hammer,
    num: "02",
    title: "Refurbishment Coordination",
    text: "We work with our development company to coordinate refurbishment and prepare properties for the rental market, keeping the process organised.",
    img: IMAGES.livingAmbient,
    alt: "Refurbished living space with warm ambient lighting",
  },
  {
    icon: KeyRound,
    num: "03",
    title: "Tenant Finding",
    text: "We work with estate agents to market the property and find tenants, keeping you informed from listing through to a successful let.",
    img: IMAGES.georgian,
    alt: "Georgian brick residence with a classic black front door",
  },
  {
    icon: Calculator,
    num: "04",
    title: "Accounting Connections",
    text: "We introduce accountants who understand property, so accounts and tax considerations are handled properly from the start.",
    img: IMAGES.dining,
    alt: "Sophisticated interior dining area with bespoke lighting",
  },
];

const Investors = () => {
  usePageMeta(
    "Property Investment Search & Support | Elin's Consultancy",
    "Property sourcing, refurbishment coordination, tenant finding and accounting connections for property investors."
  );
  const content = useContent();
  const banner = content?.banners?.investors ? fileUrl(content.banners.investors) : CINEMATIC.skylineSunset;
  return (
    <Layout>
      <PageHero
        eyebrow="For Property Investors"
        lines={[
          "Property Sourcing &",
          <>
            <em className="italic font-normal text-gold-soft">Investment</em> Support
          </>,
        ]}
        text="We help investors find suitable property opportunities and coordinate the expertise needed to turn them into successful investments — from search to rental."
        image={banner}
        imageAlt="The London skyline under vibrant golden-hour clouds"
      />

      <section data-testid="investor-pillars" className="bg-cream">
        <SectionHead
          eyebrow="Investor Services"
          title="One Coordinated Journey, From Search to Rental"
          text="Four connected services, delivered with our trusted network of professionals."
        />
        <div className="wrap-x pb-20 sm:pb-24 space-y-8">
          {PILLARS.map((p, i) => (
            <Reveal key={p.num} delay={0.05}>
              <article
                data-testid={`investor-pillar-${p.num}`}
                className={`group grid grid-cols-1 lg:grid-cols-2 bg-cream-3 border border-forest/10 overflow-hidden transition-colors duration-500 hover:border-gold/60 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="h-56 lg:h-auto overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-8 sm:p-12 lg:p-14 flex flex-col justify-center">
                  <div className="flex items-center gap-4">
                    <span className="font-serif italic text-xl text-gold-dark">{p.num}</span>
                    <span className="w-10 h-px bg-gold/50" />
                    <p.icon size={26} strokeWidth={1.3} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-medium text-forest mt-5 mb-4">{p.title}</h3>
                  <p className="text-sm leading-[1.75] text-sage max-w-md">{p.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CinematicBreak
        image={CINEMATIC.skylineDusk}
        alt="London's financial district silhouetted at dusk"
        caption="A Considered Approach"
        quote="We never promise returns. We promise a careful process, honest advice and the right professionals beside you."
      />

      <section className="bg-cream">
        <div className="wrap-x py-16 sm:py-20 text-center">
          <Reveal>
            <p className="text-sm text-sage max-w-lg mx-auto leading-relaxed">
              Ready to explore suitable opportunities? Tell us your goals and preferred locations,
              and we&rsquo;ll arrange an initial consultation.
            </p>
            <Link to="/contact" data-testid="investor-enquire-button" className="btn-forest mt-8">
              Start an Investor Enquiry <ArrowRight size={14} strokeWidth={2.2} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title={
          <>
            Let&rsquo;s Find Your Next
            <br />
            <em className="italic font-normal text-gold-soft">Investment Property</em>
          </>
        }
        text="Share your goals, budget and preferred areas — we'll help you identify suitable opportunities and coordinate everything that follows."
      />
    </Layout>
  );
};

export default Investors;
